import {
  HAKLAI_2990_DEFAULTS,
  HAKLAI_2990_SMART_ADDRESS_PARSER_TOKENS,
  HAKLAI_2990_VALIDATION_MESSAGES,
  getHaklai2990ExportGroupTitle
} from "./haklai2990Texts";

export const EMPTY_DOC_2990_FIELDS = {
  lishcatRishumLocation: "",
  sellerFirstName: "",
  sellerLastName: "",
  sellerId: "",
  assetCity: "",
  assetStreet: "",
  assetStreetNumber: "",
  blockNum: "",
  subBlock: "",
  subSubBlock: "",
  assetZipcode: "",
  assetHouseNum: "",
  passPartMekarkein: "",
  passPartMekarkeinMone: "",
  passPartMekarkeinMechane: ""
};

export const DOC_2990_NUMERIC_FIELDS = new Set([
  "blockNum",
  "subBlock",
  "subSubBlock",
  "assetZipcode",
  "assetStreetNumber",
  "assetHouseNum",
  "passPartMekarkeinMone",
  "passPartMekarkeinMechane"
]);

export const DOC_2990_AUTOFILL_TARGET_FIELDS = [
  "blockNum",
  "subBlock",
  "assetCity",
  "assetStreet",
  "assetStreetNumber",
  "assetZipcode",
  "assetHouseNum"
];

export const normalizeFieldValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

export const normalizeDocFieldValueForState = (fieldName, value) => {
  if (value === null || value === undefined || value === "") return "";
  if (!DOC_2990_NUMERIC_FIELDS.has(fieldName)) {
    return String(value);
  }

  const parsedNumber = Number(
    String(value)
      .replace(/,/g, "")
      .trim()
  );

  return Number.isFinite(parsedNumber) ? parsedNumber : "";
};

export const normalizeAddressToken = (value) => (
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[,'"]/g, "")
    .toLowerCase()
);

export const normalizeSmartAddressFragment = (value) => (
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^[,\-./\s]+|[,\-./\s]+$/g, "")
);

const SMART_ADDRESS_APARTMENT_TOKEN_PATTERN = HAKLAI_2990_SMART_ADDRESS_PARSER_TOKENS.join("|");
const SMART_ADDRESS_REGEX = new RegExp(
  `^(?<street>.+?)\\s+(?<house>\\d+)(?:(?:\\s*\\/\\s*|\\s+(?:${SMART_ADDRESS_APARTMENT_TOKEN_PATTERN})\\s+)(?<apartment>\\d+))?(?:\\s*,\\s*|\\s+)(?<city>.+)$`,
  "u"
);

export const parseSmartLookupAddress = (value) => {
  const trimmed = String(value || "").trim().replace(/\s+/g, " ");
  if (!trimmed) return null;

  const zipMatch = trimmed.match(/^(?<address>.+?)(?:\s*,?\s+(?<zip>\d{5,7}))$/u);
  const addressWithoutZip = zipMatch?.groups?.address ? zipMatch.groups.address : trimmed;
  const zipCode = zipMatch?.groups?.zip ? normalizeFieldValue(zipMatch.groups.zip).trim() : "";

  const match = addressWithoutZip.match(SMART_ADDRESS_REGEX);

  if (!match?.groups) return null;

  const street = normalizeSmartAddressFragment(match.groups.street);
  const city = normalizeSmartAddressFragment(match.groups.city);
  const houseNumber = normalizeFieldValue(match.groups.house).trim();
  const apartmentNumber = normalizeFieldValue(match.groups.apartment).trim();
  const houseBase = parseInt(houseNumber, 10);
  const apartmentBase = apartmentNumber ? parseInt(apartmentNumber, 10) : null;

  if (!street || !city || !Number.isFinite(houseBase) || houseBase <= 0) return null;
  if (/\d/.test(city)) return null;
  if (apartmentNumber && (!Number.isFinite(apartmentBase) || apartmentBase <= 0)) return null;

  return {
    street,
    houseNumber,
    city,
    apartmentNumber: apartmentNumber || "",
    zipCode: zipCode || "",
    lookupAddress: `${street} ${houseNumber}, ${city}`
  };
};

export const getSmartAddressLookupKey = (parsedAddress) => {
  if (!parsedAddress) return "";
  return [
    normalizeAddressToken(parsedAddress.street),
    normalizeAddressToken(parsedAddress.houseNumber),
    normalizeAddressToken(parsedAddress.city),
    normalizeAddressToken(parsedAddress.apartmentNumber),
    normalizeAddressToken(parsedAddress.zipCode)
  ].join("|");
};

const scoreAddressCandidate = (candidate, parsedAddressTokens) => {
  let score = 0;
  if (normalizeAddressToken(candidate.street) === parsedAddressTokens.street) score += 2;
  if (normalizeAddressToken(candidate.city) === parsedAddressTokens.city) score += 2;
  if (normalizeAddressToken(candidate.houseNumber) === parsedAddressTokens.houseNumber) score += 3;
  return score;
};

export const pickBestGovMapAddressCandidate = (candidates, parsedAddress) => {
  if (!Array.isArray(candidates) || candidates.length === 0) return null;
  if (!parsedAddress) return candidates[0];

  const parsedAddressTokens = {
    street: normalizeAddressToken(parsedAddress.street),
    city: normalizeAddressToken(parsedAddress.city),
    houseNumber: normalizeAddressToken(parsedAddress.houseNumber)
  };

  let bestCandidate = candidates[0];
  let bestScore = -1;

  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    const directCandidate = candidate;
    const swappedCandidate = {
      ...candidate,
      street: candidate.city,
      city: candidate.street
    };
    const directScore = scoreAddressCandidate(directCandidate, parsedAddressTokens);
    const swappedScore = scoreAddressCandidate(swappedCandidate, parsedAddressTokens);
    const candidateToUse = swappedScore > directScore ? swappedCandidate : directCandidate;
    const scoreToUse = Math.max(directScore, swappedScore);

    if (scoreToUse > bestScore) {
      bestScore = scoreToUse;
      bestCandidate = candidateToUse;
    }
  }

  return bestCandidate;
};

export const normalizeFetchedDocFields = (serverFields = {}) => (
  Object.keys(EMPTY_DOC_2990_FIELDS).reduce((acc, key) => {
    acc[key] = normalizeDocFieldValueForState(key, serverFields[key]);
    return acc;
  }, {})
);

export const normalizeDocFieldsForPersistence = (docFields = {}) => (
  Object.keys(EMPTY_DOC_2990_FIELDS).reduce((acc, key) => {
    acc[key] = normalizeDocFieldValueForState(key, docFields[key]);
    return acc;
  }, {})
);

export const getSeededDocFields = ({ serverFields = {}, haklaiDraftPreCalc = {} }) => {
  const normalizedServerFields = normalizeFetchedDocFields(serverFields);
  const parcelGush = normalizeDocFieldValueForState("blockNum", haklaiDraftPreCalc?.parcelGush);
  const parcelHelka = normalizeDocFieldValueForState("subBlock", haklaiDraftPreCalc?.parcelHelka);

  return {
    ...EMPTY_DOC_2990_FIELDS,
    ...normalizedServerFields,
    lishcatRishumLocation:
      normalizedServerFields.lishcatRishumLocation || HAKLAI_2990_DEFAULTS.lishcatRishumLocation,
    blockNum: normalizedServerFields.blockNum || parcelGush,
    subBlock: normalizedServerFields.subBlock || parcelHelka
  };
};

export const buildPropertyAutofillPatch = ({ lot, parcel, resolvedAddress, parsedAddress }) => {
  const patch = {
    blockNum: normalizeDocFieldValueForState("blockNum", lot),
    subBlock: normalizeDocFieldValueForState("subBlock", parcel)
  };

  const effectiveAddress = resolvedAddress || parsedAddress;

  if (effectiveAddress) {
    patch.assetCity = normalizeDocFieldValueForState("assetCity", effectiveAddress.city);
    patch.assetStreet = normalizeDocFieldValueForState("assetStreet", effectiveAddress.street);
    patch.assetStreetNumber = normalizeDocFieldValueForState(
      "assetStreetNumber",
      effectiveAddress.houseNumber
    );
  }

  if (parsedAddress?.apartmentNumber) {
    patch.assetHouseNum = normalizeDocFieldValueForState(
      "assetHouseNum",
      parsedAddress.apartmentNumber
    );
  }

  if (parsedAddress?.zipCode) {
    patch.assetZipcode = normalizeDocFieldValueForState(
      "assetZipcode",
      parsedAddress.zipCode
    );
  }

  return patch;
};

export const getParcelPairCacheKey = (lot, parcel) => `${lot}-${parcel}`;

export const getHaklai2990ExportGroups = (analyzedPreCalc) => {
  const parts = Array.isArray(analyzedPreCalc?.preCalcPartsData) ? analyzedPreCalc.preCalcPartsData : [];
  return parts.map((part, index) => ({
    index,
    title: getHaklai2990ExportGroupTitle(index, parts.length),
    needsMutav: part?.needsMutav === true,
    needsRegTax: part?.needsRegTax === true
  }));
};

const DOC_2990_REQUIRED_JSON_FIELDS = [
  "sellerFirstName",
  "sellerLastName",
  "lishcatRishumLocation",
  "sellerId",
  "blockNum",
  "subBlock",
  "assetStreet",
  "assetStreetNumber"
];

const hasRequiredJsonValue = (value) => (
  value !== null && value !== undefined && value !== ""
);

const buildJsonValidationResult = (message = "", fieldErrors = {}) => {
  const firstFieldName = Object.keys(fieldErrors)[0] || "";
  return {
    message,
    fieldErrors,
    firstFieldName
  };
};

export const validate2990JsonFieldsDetailed = (docFields = {}) => {
  const mechane = parseFloat(docFields.passPartMekarkeinMechane);
  const mone = parseFloat(docFields.passPartMekarkeinMone);

  if (!Number.isInteger(mechane) || !Number.isInteger(mone)) {
    return buildJsonValidationResult(
      HAKLAI_2990_VALIDATION_MESSAGES.fractionMustUseIntegers,
      {
        passPartMekarkeinMone: HAKLAI_2990_VALIDATION_MESSAGES.fractionMustUseIntegers,
        passPartMekarkeinMechane: HAKLAI_2990_VALIDATION_MESSAGES.fractionMustUseIntegers
      }
    );
  }

  if (mechane <= 0 || mone <= 0) {
    return buildJsonValidationResult(
      HAKLAI_2990_VALIDATION_MESSAGES.fractionMustBePositive,
      {
        passPartMekarkeinMone: HAKLAI_2990_VALIDATION_MESSAGES.fractionMustBePositive,
        passPartMekarkeinMechane: HAKLAI_2990_VALIDATION_MESSAGES.fractionMustBePositive
      }
    );
  }

  const missingFieldErrors = DOC_2990_REQUIRED_JSON_FIELDS.reduce((acc, fieldName) => {
    if (!hasRequiredJsonValue(docFields[fieldName])) {
      acc[fieldName] = HAKLAI_2990_VALIDATION_MESSAGES.missingOnlineFormFields;
    }
    return acc;
  }, {});

  if (Object.keys(missingFieldErrors).length) {
    return buildJsonValidationResult(
      HAKLAI_2990_VALIDATION_MESSAGES.missingOnlineFormFields,
      missingFieldErrors
    );
  }

  if (String(docFields.sellerId).length < 9) {
    return buildJsonValidationResult(
      HAKLAI_2990_VALIDATION_MESSAGES.invalidSellerId,
      { sellerId: HAKLAI_2990_VALIDATION_MESSAGES.invalidSellerId }
    );
  }

  const subBlockNum = parseInt(docFields.subBlock, 10);
  if (Number.isFinite(subBlockNum) && (subBlockNum > 9999 || subBlockNum < 0)) {
    return buildJsonValidationResult(
      HAKLAI_2990_VALIDATION_MESSAGES.parcelRange,
      { subBlock: HAKLAI_2990_VALIDATION_MESSAGES.parcelRange }
    );
  }

  return buildJsonValidationResult();
};

export const validate2990JsonFields = (docFields) => {
  return validate2990JsonFieldsDetailed(docFields).message;
};
